import {TabList as HUITabList} from '@headlessui/react'
import {useCallback, useEffect, useRef, useState} from 'react'

import {twMerge} from '@sdox/utils'

import {useHasOverflow, useHorizontalDragScroll} from '../../hooks'
import {TabListScrollButton} from './TabListScrollButton'

import type {ReactNode, UIEventHandler} from 'react'
import type {TabListProps as HUITabListProps} from '@headlessui/react'

export interface TabListProps extends Omit<HUITabListProps, 'className'> {
  children: ReactNode
  className?: string
}

export const TabList = (props: TabListProps) => {
  const {children, className, ...restProps} = props
  const ref = useRef<HTMLDivElement>(null)
  const {handlers} = useHorizontalDragScroll()
  const hasOverflow = useHasOverflow({ref, axis: 'x'})
  const [isScrolledStart, setIsScrolledStart] = useState(true)
  const [isScrolledEnd, setIsScrolledEnd] = useState(true)

  const updateScrollStops = useCallback(() => {
    const element = ref.current
    if (!element) return
    const {scrollLeft, scrollWidth, clientWidth} = element
    setIsScrolledStart(scrollLeft <= 0)
    setIsScrolledEnd(scrollLeft + clientWidth >= scrollWidth)
  }, [setIsScrolledStart, setIsScrolledEnd])

  // initialize scroll stops after mount and when overflow state changes
  useEffect(() => {
    updateScrollStops()
  }, [updateScrollStops, hasOverflow])

  const onScroll: UIEventHandler<HTMLDivElement> = () => {
    updateScrollStops()
  }

  return (
    <HUITabList className={'relative bg-surface-primary'}>
      <div
        onScroll={onScroll}
        ref={ref}
        className={twMerge(
          'relative flex flex-nowrap gap-spacing-4 overflow-hidden',
          className,
        )}
        {...handlers}
        {...restProps}
      >
        {children}
      </div>
      {hasOverflow ? (
        <>
          {!isScrolledStart ? (
            <TabListScrollButton
              direction={'left'}
              scrollRef={ref}
            />
          ) : null}
          {!isScrolledEnd ? (
            <TabListScrollButton
              direction={'right'}
              scrollRef={ref}
            />
          ) : null}
        </>
      ) : null}
    </HUITabList>
  )
}
